import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../api';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage, getLocale } from '../../contexts/LanguageContext';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import { toLocalInput } from '../auction/auctionUtils';
import './RecoveryPage.css';

const DAY_MS = 24 * 3600000;
const STATUSES = ['review', 'accepted', 'rejected'];
const KINDS = ['all', 'attaque', 'defense', 'tempo', 'pepite'];
// Libellés français (repli de t() pour les clés construites)
const STATUS_LABELS = { review: 'À valider', accepted: 'Acceptés', rejected: 'Refusés' };
const KIND_LABELS = { attaque: 'Attaque', defense: 'Défense', tempo: 'Tempo', pepite: 'Pépites' };

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString(getLocale(), { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const fmtNum = (n) => (n || 0).toLocaleString(getLocale());

/* ── Screen chargé à l'affichage de la carte (les liens Discord expirent : on demande un lien à jour) ── */
const Screenshot = ({ serverId, itemId }) => {
    const { t } = useLanguage();
    const ref = useRef(null);
    const [url, setUrl] = useState(null);
    const [state, setState] = useState('idle');   // idle | loading | ready | missing

    useEffect(() => {
        const node = ref.current;
        if (!node || state !== 'idle') return undefined;
        const observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            setState('loading');
            api.get(`/recovery/${serverId}/${itemId}/image`)
                .then(res => { setUrl(res.data?.url || null); setState(res.data?.url ? 'ready' : 'missing'); })
                .catch(() => setState('missing'));
        }, { rootMargin: '200px' });
        observer.observe(node);
        return () => observer.disconnect();
    }, [serverId, itemId, state]);

    return (
        <div ref={ref} className="rec-shot">
            {state === 'ready' ? (
                <a href={url} target="_blank" rel="noreferrer" title={t('recovery.openImage', 'Ouvrir le screen')}>
                    <img src={url} alt={t('recovery.screenAlt', 'Screen du message')} loading="lazy" onError={() => setState('missing')} />
                </a>
            ) : state === 'missing' ? (
                <span className="c2-muted">{t('recovery.noImage', 'Screen indisponible')}</span>
            ) : (
                <div className="c2-spinner" />
            )}
        </div>
    );
};

/* ── Ce que le bot a compris du message ── */
const Summary = ({ item }) => {
    const { t } = useLanguage();
    const d = item.data;
    if (item.kind === 'pepite') return <strong>{t('recovery.pepitesAmount', '{n} pépites', { n: fmtNum(d.amount) })}</strong>;
    if (item.kind === 'tempo') {
        return d.minutes
            ? <strong>{t('recovery.tempoMinutes', '{m} min → {p} pts', { m: d.minutes, p: d.points ?? 0 })}</strong>
            : <strong>{t('recovery.tempoLegacy', 'Tempo (ancien système, points selon le salon)')}</strong>;
    }
    const enemies = d.nombreEnnemis === -1 ? t('recovery.allEnemies', 'tous') : d.nombreEnnemis;
    return <strong>{t('recovery.combat', '{allies} alliés contre {enemies} ennemis', { allies: d.nombreAllies, enemies })}</strong>;
};

const KIND_TONE = { attaque: 'purple', defense: 'green', tempo: '', pepite: 'purple' };

const ItemCard = ({ item, serverId, selected, onSelect, onDecide, onRestore, busy }) => {
    const { t } = useLanguage();
    const players = item.kind === 'pepite'
        ? [item.data.playerId || item.authorId]
        : item.data.teamMembers;

    return (
        <li className={`c2-tile rec-item${selected ? ' is-selected' : ''}${item.duplicateOf ? ' is-duplicate' : ''}`}>
            <Screenshot serverId={serverId} itemId={item._id} />
            <div className="rec-item__body">
                <div className="rec-item__head">
                    {item.status === 'review' && (
                        <input
                            type="checkbox"
                            className="c2-check"
                            checked={selected}
                            onChange={() => onSelect(item._id)}
                            aria-label={t('recovery.select', 'Sélectionner')}
                        />
                    )}
                    <span className={`c2-chip${KIND_TONE[item.kind] ? ` c2-chip--${KIND_TONE[item.kind]}` : ''}`}>
                        {t(`recovery.kinds.${item.kind}`, KIND_LABELS[item.kind])}
                    </span>
                    <Summary item={item} />
                </div>

                <p className="rec-item__meta c2-muted">
                    #{item.channelName} · {fmtDateTime(item.postedAt)} · {t('recovery.by', 'par {name}', { name: item.authorName })}
                </p>

                {players.length > 0 && (
                    <div className="rec-item__players">
                        {players.map(id => <span key={id} className="c2-chip">{item.names[id] || `${id.slice(0, 8)}…`}</span>)}
                    </div>
                )}

                {item.content && <p className="rec-item__content">{item.content.replace(/<@!?(\d+)>/g, (m, id) => `@${item.names[id] || id}`)}</p>}

                {item.duplicateOf && (
                    <p className="rec-item__warning">
                        <Icon name="copy" size={14} />
                        {t('recovery.duplicate', 'Ressemble à un envoi déjà compté le {date}', { date: fmtDateTime(item.duplicateOf.postedAt) })}
                        {item.duplicateOf.messageUrl && (
                            <a href={item.duplicateOf.messageUrl} target="_blank" rel="noreferrer">{t('recovery.see', 'voir')}</a>
                        )}
                    </p>
                )}

                <div className="rec-item__actions">
                    <a href={item.messageUrl} target="_blank" rel="noreferrer" className="c2-btn c2-btn--ghost c2-btn--sm">
                        {t('recovery.openDiscord', 'Voir sur Discord')}
                        <Icon name="arrowRight" size={14} />
                    </a>
                    {item.status === 'review' && (
                        <>
                            <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={() => onDecide([item._id], 'reject')} disabled={busy}>
                                <Icon name="close" size={14} />
                                {t('recovery.reject', 'Refuser')}
                            </button>
                            <button type="button" className="c2-btn c2-btn--primary c2-btn--sm" onClick={() => onDecide([item._id], 'accept')} disabled={busy}>
                                <Icon name="check" size={14} />
                                {t('recovery.accept', 'Accepter')}
                            </button>
                        </>
                    )}
                    {item.status === 'rejected' && (
                        <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={() => onRestore(item._id)} disabled={busy}>
                            <Icon name="refresh" size={14} />
                            {t('recovery.restore', 'Remettre à valider')}
                        </button>
                    )}
                    {item.status !== 'review' && item.decidedAt && (
                        <span className="c2-muted rec-item__decided">
                            {item.status === 'accepted'
                                ? t('recovery.acceptedOn', 'Accepté le {date}', { date: fmtDateTime(item.decidedAt) })
                                : t('recovery.rejectedOn', 'Refusé le {date}', { date: fmtDateTime(item.decidedAt) })}
                        </span>
                    )}
                </div>
            </div>
        </li>
    );
};

/**
 * Rattrapage : messages des salons surveillés que le bot n'a pas enregistrés (bot éteint, coupure...).
 * Rien ne compte tant qu'ils ne sont pas acceptés ici.
 */
const RecoveryPage = () => {
    const { serverId } = useParams();
    const { t } = useLanguage();
    const { success, error: showError } = useNotification();

    const [status, setStatus] = useState('review');
    const [kind, setKind] = useState('all');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(() => new Set());
    const [busy, setBusy] = useState(false);
    const [range, setRange] = useState(() => ({ from: toLocalInput(Date.now() - DAY_MS), to: toLocalInput(Date.now()) }));
    const [scan, setScan] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        return api.get(`/recovery/${serverId}`, { params: { status } })
            .then(res => { setData(res.data); setScan(res.data.scan); })
            .catch(() => showError(t('recovery.loadError', 'Impossible de charger les messages à valider')))
            .finally(() => setLoading(false));
    }, [serverId, status, showError, t]);

    useEffect(() => { load(); setSelected(new Set()); }, [load]);

    // Suivi de la recherche en cours
    const scanRunning = !!scan?.running;
    useEffect(() => {
        if (!scanRunning) return undefined;
        const id = setInterval(() => {
            api.get(`/recovery/${serverId}/scan`)
                .then(res => {
                    setScan(res.data.scan);
                    if (!res.data.scan?.running) load();
                })
                .catch(() => {});
        }, 2000);
        return () => clearInterval(id);
    }, [scanRunning, serverId, load]);

    const startScan = async (e) => {
        e.preventDefault();
        try {
            const res = await api.post(`/recovery/${serverId}/scan`, {
                from: new Date(range.from).toISOString(),
                to: new Date(range.to).toISOString()
            });
            setScan(res.data.scan);
        } catch (err) {
            showError(err?.response?.data?.error || t('recovery.scanError', 'Impossible de lancer la recherche'));
        }
    };

    const items = (data?.items || []).filter(item => kind === 'all' || item.kind === kind);
    const visibleIds = items.map(item => item._id);
    const selectedVisible = visibleIds.filter(id => selected.has(id));
    const allSelected = visibleIds.length > 0 && selectedVisible.length === visibleIds.length;

    const toggle = (id) => setSelected(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(visibleIds));

    const decide = async (ids, decision) => {
        if (ids.length === 0) return;
        const duplicates = items.filter(item => ids.includes(item._id) && item.duplicateOf).length;
        if (decision === 'accept' && duplicates > 0 && !window.confirm(
            t('recovery.confirmDuplicates', '{n} message(s) ressemblent à des envois déjà comptés (doublons probables). Les accepter quand même ?', { n: duplicates })
        )) return;
        if (ids.length > 1 && !window.confirm(decision === 'accept'
            ? t('recovery.confirmAccept', 'Accepter {n} messages ? Ils seront comptés comme s\'ils avaient été enregistrés à leur date d\'envoi.', { n: ids.length })
            : t('recovery.confirmReject', 'Refuser {n} messages ?', { n: ids.length }))) return;

        setBusy(true);
        try {
            // Par paquets de 100 (limite de l'API)
            let done = 0;
            let failed = 0;
            for (let i = 0; i < ids.length; i += 100) {
                const res = await api.post(`/recovery/${serverId}/decide`, { ids: ids.slice(i, i + 100), decision });
                done += res.data.done.length;
                failed += res.data.failed.length;
            }
            if (done > 0) {
                success(decision === 'accept'
                    ? t('recovery.accepted', '{n} message(s) accepté(s) et comptabilisé(s)', { n: done })
                    : t('recovery.rejected', '{n} message(s) refusé(s)', { n: done }));
            }
            if (failed > 0) showError(t('recovery.failed', '{n} message(s) n\'ont pas pu être traités', { n: failed }));
            setSelected(new Set());
            await load();
        } catch (err) {
            showError(err?.response?.data?.error || t('recovery.decideError', 'Erreur lors de l\'enregistrement de la décision'));
        } finally {
            setBusy(false);
        }
    };

    const restore = async (id) => {
        setBusy(true);
        try {
            await api.post(`/recovery/${serverId}/${id}/restore`);
            await load();
        } catch {
            showError(t('recovery.restoreError', 'Impossible de remettre ce message à valider'));
        } finally {
            setBusy(false);
        }
    };

    const counts = data?.counts || {};
    const result = scan?.result;

    return (
        <div className="c2-scope c2-admin-page">
            <PageHeader
                icon="history"
                title={t('navigation.recovery', 'Rattrapage')}
                subtitle={t('recovery.subtitle', 'Screens et pépites postés pendant que le bot ne les voyait pas : rien ne compte avant validation')}
                actions={
                    <button type="button" className="c2-btn c2-btn--ghost" onClick={load} disabled={loading}>
                        <Icon name="refresh" size={18} />
                        {t('common.refresh', 'Actualiser')}
                    </button>
                }
            />

            {/* ── Recherche ── */}
            <section className="c2-card c2-section">
                <h2 className="c2-section__title"><Icon name="search" size={20} />{t('recovery.scanTitle', 'Rechercher les messages manqués')}</h2>
                <p className="c2-section__desc">
                    {t('recovery.scanDesc', 'Le bot relit tous les salons configurés (attaque, défense, tempo, pépites) sur la période choisie et place ici les messages qu\'il n\'a pas enregistrés. Il le fait aussi tout seul à chaque redémarrage.')}
                </p>
                <form className="c2-row rec-scan" onSubmit={startScan}>
                    <label className="c2-field">
                        <span className="c2-field__label">{t('recovery.from', 'Du')}</span>
                        <input type="datetime-local" className="c2-input" value={range.from} onChange={e => setRange(r => ({ ...r, from: e.target.value }))} required />
                    </label>
                    <label className="c2-field">
                        <span className="c2-field__label">{t('recovery.to', 'Au')}</span>
                        <input type="datetime-local" className="c2-input" value={range.to} onChange={e => setRange(r => ({ ...r, to: e.target.value }))} required />
                    </label>
                    <button type="submit" className="c2-btn c2-btn--accent" disabled={scanRunning}>
                        <Icon name="search" size={18} />
                        {scanRunning ? t('recovery.scanning', 'Recherche en cours...') : t('recovery.scan', 'Lancer la recherche')}
                    </button>
                </form>
                <p className="c2-field__hint">{t('recovery.maxRange', 'Période de {n} jours maximum.', { n: data?.maxRangeDays || 7 })}</p>

                {scan && (
                    <div className={`rec-scan-status${scan.error ? ' is-error' : ''}`} role="status">
                        {scan.running ? (
                            <><div className="c2-spinner" /><span>{t('recovery.scanRunning', 'Recherche du {from} au {to}...', { from: fmtDateTime(scan.from), to: fmtDateTime(scan.to) })}</span></>
                        ) : scan.error ? (
                            <span>{t('recovery.scanFailed', 'La recherche a échoué : {error}', { error: scan.error })}</span>
                        ) : result && (
                            <span>
                                <Icon name="check" size={16} />
                                {t('recovery.scanDone', '{channels} salons relus, {scanned} messages lus, {found} nouveau(x) à valider', result)}
                                {result.errors?.length > 0 && ` · ${t('recovery.scanChannelErrors', '{n} salon(s) inaccessible(s)', { n: result.errors.length })}`}
                            </span>
                        )}
                    </div>
                )}
                {data?.lastAutoRecovery && (
                    <p className="c2-field__hint">
                        {t('recovery.lastAuto', 'Dernier rattrapage automatique (redémarrage du bot) : {date}', { date: fmtDateTime(data.lastAutoRecovery.at) })}
                    </p>
                )}
            </section>

            {/* ── Messages trouvés ── */}
            <section className="c2-card c2-section">
                <div className="c2-section__head">
                    <div className="rec-tabs" role="tablist" aria-label={t('recovery.statusLabel', 'Statut')}>
                        {STATUSES.map(s => (
                            <button key={s} type="button" role="tab" aria-selected={status === s} className={status === s ? 'is-active' : undefined} onClick={() => setStatus(s)}>
                                {t(`recovery.status.${s}`, STATUS_LABELS[s])}
                                <span className="rec-tabs__count">{counts[s] || 0}</span>
                            </button>
                        ))}
                    </div>
                    <div className="rec-tabs rec-tabs--small" role="tablist" aria-label={t('recovery.kindLabel', 'Type')}>
                        {KINDS.map(k => (
                            <button key={k} type="button" role="tab" aria-selected={kind === k} className={kind === k ? 'is-active' : undefined} onClick={() => setKind(k)}>
                                {k === 'all' ? t('recovery.allKinds', 'Tout') : t(`recovery.kinds.${k}`, KIND_LABELS[k])}
                            </button>
                        ))}
                    </div>
                </div>

                {status === 'review' && items.length > 0 && (
                    <div className="rec-bulk">
                        <label className="rec-bulk__all">
                            <input type="checkbox" className="c2-check" checked={allSelected} onChange={toggleAll} />
                            {t('recovery.selectAll', 'Tout sélectionner ({n})', { n: items.length })}
                        </label>
                        <div className="c2-row">
                            <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={() => decide(selectedVisible, 'reject')} disabled={busy || selectedVisible.length === 0}>
                                {t('recovery.rejectSelected', 'Refuser la sélection ({n})', { n: selectedVisible.length })}
                            </button>
                            <button type="button" className="c2-btn c2-btn--primary c2-btn--sm" onClick={() => decide(selectedVisible, 'accept')} disabled={busy || selectedVisible.length === 0}>
                                <Icon name="check" size={16} />
                                {t('recovery.acceptSelected', 'Accepter la sélection ({n})', { n: selectedVisible.length })}
                            </button>
                        </div>
                    </div>
                )}

                {loading && !data ? (
                    <div className="c2-loading"><div className="c2-spinner" /><p>{t('common.loading', 'Chargement...')}</p></div>
                ) : items.length === 0 ? (
                    <div className="c2-empty">
                        <Icon name="check" size={36} />
                        <p>{status === 'review'
                            ? t('recovery.emptyReview', 'Aucun message à valider : le bot n\'a rien manqué.')
                            : t('recovery.empty', 'Aucun message.')}</p>
                    </div>
                ) : (
                    <ul className="rec-list">
                        {items.map(item => (
                            <ItemCard
                                key={item._id}
                                item={item}
                                serverId={serverId}
                                selected={selected.has(item._id)}
                                onSelect={toggle}
                                onDecide={decide}
                                onRestore={restore}
                                busy={busy}
                            />
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
};

export default RecoveryPage;
